import CalendarIcon from "../../../../../assets/svg/calendar-icon";
import NetworkIcon from "../../../../../assets/svg/network-icon";
import LocationIcon from "../../../../../assets/svg/location-icon";
import HomeIcon from "../../../../../assets/svg/home-icon";
import TagsIcon from "../../../../../assets/svg/tags-icon";
import RoomTypeIcon from "../../../../../assets/svg/room-type-icon";
import ProfileTwoUser from "../../../../../assets/svg/v1/profile-two-user";
import ViewingsIcon from "../../../../../assets/svg/viewings-icon";
import RepeatLeadIcon from "../../../../../assets/svg/repeat-lead-icon";
import TowerIcon from "../../../../../assets/svg/TowerIcon";
import StudentIcon from "../../../../../assets/svg/student-icon";
import InvestorIcon from "../../../../../assets/svg/investor-icon";
import UniversityIcon from "../../../../../assets/svg/university-icon";
import LeadStageIcon from "../../../../../assets/svg/lead-stage-icon";
import { getClientCRMFlag } from "../../../../../utils/constants/clientCustomCRMFlags";
import { getRegionLabel } from "../../../../../utils/constants/mappings";
import type {
  FilterFieldOption,
  FilterGroupUI,
  FilterConditionUI,
  ViewFormValues,
} from "../../../../Crm/components/HelpDesk/components/CreateView/utils/constants";
import WorkflowIcon from "../../../../../assets/svg/workflow-icon";
import GlobeOneIcon from "../../../../../assets/svg/globe-one-icon";
import UtmMediumIcon from "../../../../../assets/svg/utm-medium-icon";
import DeviceTypeIcon from "../../../../../assets/svg/device-type-icon";
import { ACADEMIC_YEAR_OPTIONS } from "../../../../../components/Filters/utils/constants";

export {
  type FilterConditionUI,
  type FilterGroupUI,
  type ViewFormValues,
  INITIAL_VIEW_FORM_VALUES,
  createEmptyCondition,
  createEmptyGroup,
  DURATION_OPTIONS,
  type SharedWith,
  SHARED_WITH_OPTIONS,
  SHARED_WITH_LABELS,
  type ComparisonOperator,
} from "../../../../Crm/components/HelpDesk/components/CreateView/utils/constants";

export { viewFormValidationSchema } from "../../../../Crm/components/HelpDesk/components/CreateView/utils/functions";

const CRM_BOOLEAN_FIELDS = ["viewings", "exclude_repeat_leads"];
const CRM_INTEGER_ARRAY_FIELDS = [
  "destination_country",
  "destination_city",
  "property",
];
const CRM_INTEGER_SINGLE_FIELDS = ["university"];

function getConditionPayloadValue(c: FilterConditionUI, currency?: string) {
  if (c.key === "budget") return budgetKeyToPayload(c.value?.[0], currency);
  if (c.key === "lease_duration")
    return leaseDurationKeyToPayload(c.value?.[0]);
  if (CRM_BOOLEAN_FIELDS.includes(c.key)) return c.value?.[0] === "true";
  if (c.comparison_operator === "exists") return c.value?.[0] === "true";
  // Free-text fields send a single string on `contains` / `not_contains`
  // (their UI is a text box). `contains_any` / `not_in` fall through to the
  // array handling below so they keep sending arrays from the multi-select.
  if (
    (c.comparison_operator === "contains" ||
      c.comparison_operator === "not_contains") &&
    CRM_FREE_TEXT_FIELDS.includes(c.key)
  ) {
    return c.value?.[0];
  }
  if (CRM_INTEGER_ARRAY_FIELDS.includes(c.key))
    return c.value?.map((v) => parseInt(v, 10)).filter((n) => !isNaN(n));
  if (CRM_INTEGER_SINGLE_FIELDS.includes(c.key)) {
    const num = parseInt(c.value?.[0], 10);
    return isNaN(num) ? c.value?.[0] : num;
  }
  if (CRM_ARRAY_FIELDS.includes(c.key)) return c.value;
  return c.value?.[0];
}

export function buildFilterGroupsPayload(
  filterGroups: ViewFormValues["filter_groups"],
  currency?: string
) {
  return (filterGroups ?? [])
    .filter((group: FilterGroupUI) =>
      group?.conditions?.some(
        (c: FilterConditionUI) => c?.key && c?.value?.length > 0
      )
    )
    .map((group: FilterGroupUI) => ({
      conditions: (group?.conditions ?? [])
        .filter((c: FilterConditionUI) => c?.key && c?.value?.length > 0)
        .map((c: FilterConditionUI) => ({
          key: c.key,
          comparison_operator: c.comparison_operator,
          value: getConditionPayloadValue(c, currency),
          is_custom_field: c?.is_custom_field ?? false,
        })),
      logical_operator: group?.logical_operator ?? "and",
    }));
}

export const getCrmViewFilterFieldOptions = (
  clientCode?: string
): FilterFieldOption[] =>
  [
    { key: "duration", label: "Duration", icon: CalendarIcon },
    {
      key: "communication_type",
      label: "Type of Communication",
      icon: NetworkIcon,
    },
    { key: "source_country", label: "Source Country", icon: NetworkIcon },
    {
      key: "destination_country",
      label: "Destination Country",
      icon: LocationIcon,
    },
    { key: "destination_city", label: "Destination City", icon: LocationIcon },
    { key: "university", label: "University", icon: LocationIcon },
    { key: "assigned_agents", label: "Assigned Agents", icon: ProfileTwoUser },
    { key: "level_of_study", label: "Level of Study", icon: StudentIcon },
    {
      key: "current_stage",
      label: getClientCRMFlag("useContactLabel")
        ? "Contact Stage"
        : "Lead Stage",
      icon: LeadStageIcon,
    },
    { key: "lease_duration", label: "Lease Duration", icon: CalendarIcon },
    { key: "academic_year", label: "Academic Year", icon: UniversityIcon },
    { key: "room_type", label: "Room Type", icon: RoomTypeIcon },
    { key: "utm_source", label: "UTM Source", icon: NetworkIcon },
    { key: "utm_medium", label: "UTM Medium", icon: TowerIcon },
    { key: "utm_workflow", label: "UTM Workflow", icon: WorkflowIcon },
    {
      key: "property",
      label: getClientCRMFlag("useNeighbourhoodLabel")
        ? "Neighbourhood"
        : "Property",
      icon: HomeIcon,
    },
    { key: "budget", label: "Budget per week", icon: CalendarIcon },
    { key: "lead_type", label: "Lead Type", icon: ProfileTwoUser },
    {
      key: "viewings",
      label: getRegionLabel("viewings", clientCode),
      icon: ViewingsIcon,
    },
    { key: "tags", label: "Tags", icon: TagsIcon },
    {
      key: "exclude_repeat_leads",
      label: "Exclude Repeat Leads",
      icon: RepeatLeadIcon,
    },
    { key: "investor", label: "Investors", icon: InvestorIcon },
  ].map((field) => ({ ...field, section: "lead" as const }));

export const CRM_VIEW_FILTER_FIELD_OPTIONS: FilterFieldOption[] =
  getCrmViewFilterFieldOptions();

// Attribution fields are offered in CRM views only (not Campaign Segments),
// so they are passed via extraFieldOptions in CrmCreateEditView instead of
// living in the shared base list above. To enable them in Segments later,
// move these entries into getCrmViewFilterFieldOptions.
export const CRM_ATTRIBUTION_FIELD_OPTIONS: FilterFieldOption[] = [
  {
    key: "attribution_channel",
    label: "Attribution Channel",
    icon: GlobeOneIcon,
    insertAfterKey: "utm_workflow",
    section: "lead",
  },
  {
    key: "standard_medium",
    label: "Standard Medium",
    icon: UtmMediumIcon,
    insertAfterKey: "attribution_channel",
    section: "lead",
  },
  {
    key: "standard_source",
    label: "Standard Source",
    icon: NetworkIcon,
    insertAfterKey: "standard_medium",
    section: "lead",
  },
  {
    key: "device_type",
    label: "Device Type",
    icon: DeviceTypeIcon,
    insertAfterKey: "standard_source",
    section: "lead",
  },
];

export const CRM_FILTER_FIELD_OPERATORS: Record<
  string,
  { key: string; label: string }[]
> = {
  duration: [{ key: "equals", label: "Equals" }],
  communication_type: [{ key: "equals", label: "Is" }],
  source_country: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  destination_country: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  destination_city: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  university: [{ key: "equals", label: "Is" }],
  assigned_agents: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  tags: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  room_type: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  property: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
    { key: "exists", label: "Exists" },
  ],
  lead_type: [{ key: "equals", label: "Is" }],
  viewings: [{ key: "equals", label: "Is" }],
  exclude_repeat_leads: [{ key: "equals", label: "Is" }],
  utm_source: [
    { key: "contains", label: "Contains" },
    { key: "not_contains", label: "Excludes" },
  ],
  utm_medium: [
    { key: "contains", label: "Contains" },
    { key: "not_contains", label: "Excludes" },
  ],
  attribution_channel: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  standard_medium: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  standard_source: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  device_type: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  utm_workflow: [{ key: "equals", label: "Is" }],
  budget: [{ key: "range", label: "Is" }],
  lease_duration: [{ key: "range", label: "Is" }],
  level_of_study: [{ key: "equals", label: "Is" }],
  lead_category: [{ key: "equals", label: "Is" }],
  investor: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  academic_year: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
  current_stage: [
    { key: "contains", label: "Is" },
    { key: "not_contains", label: "Is not" },
  ],
};

// Leasing fields whose value is a typeable free-form string. On
// `contains` / `not_contains` they render a text box and send a single string;
// on `contains_any` / `not_in` they fall back to a multi-select (array).
export const CRM_FREE_TEXT_FIELDS = ["utm_source", "utm_medium"];

// Extra multi-select operators offered in the Leasing view, in addition to the
// base Is / Is not, for every field that supports them.
const CRM_MULTI_SELECT_EXTRA_OPERATORS = [
  { key: "contains_any", label: "Any of" },
  { key: "not_in", label: "None of" },
];

// Leasing operator catalogue: base operators plus `Any of` / `None of` for any
// field that already exposes `contains` / `not_contains` (i.e. the
// multi-selectable fields). Passed to the Leasing create-view via
// `extraOperators` so other CRM consumers stay on the base set.
export const CRM_LEASING_FILTER_FIELD_OPERATORS: Record<
  string,
  { key: string; label: string }[]
> = Object.fromEntries(
  Object.entries(CRM_FILTER_FIELD_OPERATORS).map(([key, operators]) => {
    const supportsMultiSelect = operators.some(
      (operator) =>
        operator.key === "contains" || operator.key === "not_contains"
    );
    return supportsMultiSelect
      ? [key, [...operators, ...CRM_MULTI_SELECT_EXTRA_OPERATORS]]
      : [key, operators];
  })
);

export const CRM_COMMUNICATION_TYPE_OPTIONS = [
  { key: "all", label: "All" },
  { key: "inbound", label: "Inbound" },
  { key: "outbound", label: "Outbound" },
];

export const CRM_LEAD_TYPE_OPTIONS = [
  { key: "working professional", label: "Professional" },
  { key: "student", label: "Student" },
];

export const CRM_ROOM_TYPE_OPTIONS = [
  { key: "non_ensuite", label: "Non-Ensuite" },
  { key: "ensuite", label: "Ensuite" },
  { key: "shared", label: "Shared" },
  { key: "studio", label: "Studio" },
  { key: "apartment", label: "Apartment" },
  { key: "penthouse", label: "Penthouse" },
  { key: "enhanced", label: "Enhanced" },
  { key: "elite", label: "Elite" },
  { key: "duo", label: "Duo" },
  { key: "essential", label: "Essential" },
  { key: "essential_extra", label: "Essential Extra" },
];

export const CRM_BOOLEAN_OPTIONS = [
  { key: "true", label: "Yes" },
  { key: "false", label: "No" },
];

export const CRM_BUDGET_OPTIONS = [
  { key: "0-150", label: "£0 – £150" },
  { key: "150-200", label: "£150 – £200" },
  { key: "200-250", label: "£200 – £250" },
  { key: "250-300", label: "£250 – £300" },
  { key: "300-400", label: "£300 – £400" },
  { key: "400+", label: "£400+" },
];

export const CRM_LEASE_DURATION_OPTIONS = [
  { key: "8-12w", label: "8 – 12 weeks" },
  { key: "20-30w", label: "20 – 30 weeks" },
  { key: "34-52w", label: "34 – 52 weeks" },
  { key: "3-6m", label: "3 – 6 months" },
  { key: "6-12m", label: "6 – 12 months" },
];

export const CRM_LEVEL_OF_STUDY_OPTIONS = [
  { key: "UG", label: "Undergraduate" },
  { key: "PG", label: "Postgraduate" },
  { key: "PhD", label: "Doctor of Philosophy" },
  { key: "Non Student", label: "Non-Student" },
];

export const CRM_LEAD_CATEGORY_OPTIONS = [
  { key: "lead", label: "Lead" },
  { key: "anonymous", label: "Anonymous" },
];

export const CRM_ACADEMIC_YEAR_OPTIONS = ACADEMIC_YEAR_OPTIONS.map(
  (option) => ({ key: option.value, label: option.content })
);

export const BUDGET_RANGE_MAP: Record<
  string,
  { min_budget: number; max_budget: number }
> = {
  "0-150": { min_budget: 0, max_budget: 150 },
  "150-200": { min_budget: 150, max_budget: 200 },
  "200-250": { min_budget: 200, max_budget: 250 },
  "250-300": { min_budget: 250, max_budget: 300 },
  "300-400": { min_budget: 300, max_budget: 400 },
  "400+": { min_budget: 400, max_budget: 999999 },
};

export const LEASE_DURATION_RANGE_MAP: Record<
  string,
  { min: number; max: number; unit: string }
> = {
  "8-12w": { min: 8, max: 12, unit: "weeks" },
  "20-30w": { min: 20, max: 30, unit: "weeks" },
  "34-52w": { min: 34, max: 52, unit: "weeks" },
  "3-6m": { min: 3, max: 6, unit: "months" },
  "6-12m": { min: 6, max: 12, unit: "months" },
};

export function budgetKeyToPayload(key: string, currency?: string) {
  const range = BUDGET_RANGE_MAP[key];
  if (!range) return key;
  return { ...range, currency: currency || "GBP" };
}

export function leaseDurationKeyToPayload(key: string) {
  return LEASE_DURATION_RANGE_MAP[key] || key;
}

type BudgetPayload = {
  min_budget: number;
  max_budget: number;
  currency?: string;
};
type LeaseDurationPayload = { min: number; max: number; unit: string };
type ConditionValue =
  | string
  | number
  | boolean
  | string[]
  | BudgetPayload
  | LeaseDurationPayload
  | null
  | undefined;

interface RawFilterCondition {
  key?: string;
  comparison_operator?: string;
  value?: ConditionValue;
  is_custom_field?: boolean;
}

interface RawFilterGroup {
  conditions?: RawFilterCondition[];
  logical_operator?: "and" | "or";
}

export function budgetPayloadToKey(value: ConditionValue): string {
  if (!value || typeof value !== "object" || Array.isArray(value))
    return String(value ?? "");
  if (!("min_budget" in value)) return "";
  for (const [key, range] of Object.entries(BUDGET_RANGE_MAP)) {
    if (
      (value as BudgetPayload).min_budget === range.min_budget &&
      (value as BudgetPayload).max_budget === range.max_budget
    )
      return key;
  }
  return "";
}

export function leaseDurationPayloadToKey(value: ConditionValue): string {
  if (!value || typeof value !== "object" || Array.isArray(value))
    return String(value ?? "");
  if (!("min" in value)) return "";
  for (const [key, range] of Object.entries(LEASE_DURATION_RANGE_MAP)) {
    if (
      (value as LeaseDurationPayload).min === range.min &&
      (value as LeaseDurationPayload).max === range.max &&
      (value as LeaseDurationPayload).unit === range.unit
    )
      return key;
  }
  return "";
}

export const CRM_VALUE_OPTIONS_MAP: Record<
  string,
  { key: string; label: string }[]
> = {
  communication_type: CRM_COMMUNICATION_TYPE_OPTIONS,
  lead_type: CRM_LEAD_TYPE_OPTIONS,
  room_type: CRM_ROOM_TYPE_OPTIONS,
  viewings: CRM_BOOLEAN_OPTIONS,
  exclude_repeat_leads: CRM_BOOLEAN_OPTIONS,
  budget: CRM_BUDGET_OPTIONS,
  lease_duration: CRM_LEASE_DURATION_OPTIONS,
  level_of_study: CRM_LEVEL_OF_STUDY_OPTIONS,
  lead_category: CRM_LEAD_CATEGORY_OPTIONS,
  academic_year: CRM_ACADEMIC_YEAR_OPTIONS,
};

export const getCrmOperatorsForField = (
  fieldKey: string
): { key: string; label: string }[] => {
  return CRM_FILTER_FIELD_OPERATORS[fieldKey] || [];
};

export const CRM_SINGLE_VALUE_FIELDS = [
  "duration",
  "communication_type",
  "university",
  "lead_type",
  "viewings",
  "exclude_repeat_leads",
  "utm_workflow",
  "budget",
  "lease_duration",
  "level_of_study",
  "lead_category",
];

export const CRM_ARRAY_FIELDS = [
  "source_country",
  "destination_country",
  "destination_city",
  "assigned_agents",
  "tags",
  "room_type",
  "property",
  "utm_source",
  "utm_medium",
  "attribution_channel",
  "standard_medium",
  "standard_source",
  "device_type",
  "investor",
  "academic_year",
  "current_stage",
];

export const CRM_FIELD_DEPENDENCIES: Record<
  string,
  { parentField: string; tooltip: string }
> = {
  destination_city: {
    parentField: "destination_country",
    tooltip: "Please select a destination country first",
  },
  university: {
    parentField: "destination_city",
    tooltip: "Please select a destination city first",
  },
};

export const getSelectedValuesForField = (
  filterGroups: FilterGroupUI[],
  fieldKey: string
): string[] => {
  return filterGroups.flatMap((g) =>
    g.conditions
      .filter((c) => c.key === fieldKey && c.value.length > 0)
      .flatMap((c) => c.value)
  );
};

function normalizeConditionValue(key: string, value: ConditionValue): string[] {
  if (
    key === "budget" &&
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    const uiKey = budgetPayloadToKey(value);
    return uiKey ? [uiKey] : [];
  }
  if (
    key === "lease_duration" &&
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
  ) {
    const uiKey = leaseDurationPayloadToKey(value);
    return uiKey ? [uiKey] : [];
  }
  if (Array.isArray(value)) return value.map(String);
  if (value != null) return [String(value)];
  return [];
}

export function normalizeFilterGroups(
  raw: RawFilterGroup[] | undefined
): FilterGroupUI[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((group: RawFilterGroup) => ({
    conditions: (group?.conditions ?? []).map((c: RawFilterCondition) => ({
      key: c?.key ?? "",
      comparison_operator: c?.comparison_operator ?? "contains",
      value: normalizeConditionValue(c?.key ?? "", c?.value),
      is_custom_field: c?.is_custom_field ?? false,
    })),
    logical_operator: group?.logical_operator ?? "and",
  }));
}
